import React from 'react';
import { compose } from 'redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import PropTypes from 'prop-types';
import _map from 'lodash/map';
import _split from 'lodash/split';
import _toString from 'lodash/toString';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mScenario from 'models/scenario';
// Custom
import { scenarioDataShape, deviceDataShape } from 'constants/PropTypesShapes';
import withDeviceData from 'appAssets/hocs/WithDeviceData';
import RowDetailsTemplate from 'components/templates/RowDetailsTemplate';
import { DuplicateTextButton } from 'components/buttons/TextButtons';
import InformationItem from 'components/items/InformationItem';
import { SuccessConditionItem } from 'components/items/InformationItems';
import Callout from 'components/misc/Callout';
import withEntitiesInformation from 'installations/assets/hocs/WithEntitiesInformation';
import ActionItem from './ActionItem';
import ConditionInputItem from './ConditionInputItem';
import TriggerInputItem from './TriggerInputItem';
import ScenarioTriggerItem from './ScenarioTriggerItem';
import ScenarioTriggerButton from './ScenarioTriggerButton';

const ScenarioRowDetails = ({
  scenario, deviceData, onDuplicate, onTrigger,
}) => {
  const actions = mScenario.sortActions(scenario.actions);
  const inputConditionObj = scenario.input_condition;
  let inputConditions;
  if (inputConditionObj) {
    inputConditions = _split(inputConditionObj.expression, ';');
  }
  const inputTriggerObj = scenario.input_trigger;
  let inputTriggers;
  if (inputTriggerObj) {
    inputTriggers = _split(inputTriggerObj.expression, ';');
  }

  const scenarioTriggers = mScenario.sortTriggers(scenario.scenario_triggers);

  // Permissions
  const { pInstallation: { configWrite, scenarioTrigger } } = permissions;

  return (
    <RowDetailsTemplate
      buttons={[
        <OnlyWith
          key="bDup"
          permissions={{
            needed: configWrite,
            permissionsOrContext: 'installationPermissions',
          }}
        >
          <DuplicateTextButton primary onClick={() => onDuplicate(scenario)} />
        </OnlyWith>,
        <OnlyWith
          key="bTrig"
          permissions={{
            needed: scenarioTrigger,
            permissionsOrContext: 'installationPermissions',
          }}
        >
          <ScenarioTriggerButton id={scenario.id} onTrigger={() => onTrigger(scenario)} />
        </OnlyWith>,
      ]}
    >
      <div className="row px-3 information-item-wrapper">
        <div className="col-lg-4 col-md-6 col-sm-12">
          <SuccessConditionItem
            value={<LocalizedMessage id={`scenario.successCondition.${scenario.success_condition}`} />}
          />
        </div>
        <div className="col-lg-4 col-md-6 col-sm-12">
          <InformationItem
            label=""
            value={<LocalizedMessage id="field.selfExclusion" />}
            icon={scenario.self_exclusion ? 'checkActive' : 'checkInactive'}
          />
        </div>
      </div>
      <div className="pb-3 px-3">
        {!!scenarioTriggers && scenarioTriggers.length > 0 && (
          <div className="pt-4">
            <Callout helpTooltip="tooltip.triggerMeans">
              <LocalizedMessage id="subtitle.triggerMeans" />
            </Callout>
            <div className="row">
              {_map(scenarioTriggers, (scTrigger) => (
                <div key={_toString(scTrigger.id)} className="col-sm-12 col-md-6 col-lg-4 pt-3">
                  <div className="box box-default m-0 p-2">
                    <ScenarioTriggerItem device={deviceData} scenarioTrigger={scTrigger} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {!!actions && actions.length > 0 && (
          <div className="pt-4">
            <Callout helpTooltip="tooltip.actions">
              <LocalizedMessage id="subtitle.actions" />
            </Callout>
            <div className="row">
              {_map(actions, (action) => (
                <div key={_toString(action.id)} className="col-12 col-lg-6 pt-3">
                  <div className="box box-dark m-0">
                    <ActionItem action={action} device={deviceData} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {inputTriggerObj && (
          <div className="pt-4">
            <Callout helpTooltip="tooltip.inputTriggers">
              <LocalizedMessage id="subtitle.inputTriggers" />
            </Callout>
            <div className="row">
              {_map(inputTriggers, (inputTrigger) => (
                <div key={inputTrigger} className="col-sm-12 col-md-6 col-lg-4">
                  <TriggerInputItem inputTrigger={inputTrigger} device={deviceData} />
                </div>
              ))}
            </div>
          </div>
        )}
        {inputConditionObj && (
          <div className="pt-4">
            <Callout helpTooltip="tooltip.inputConditions">
              <LocalizedMessage id="subtitle.inputConditions" />
            </Callout>
            <div className="row">
              {!!inputConditions
                && _map(inputConditions, (inputCondition) => (
                  <div key={inputCondition} className="col-sm-12 col-md-6 col-lg-4">
                    <ConditionInputItem inputCondition={inputCondition} device={deviceData} />
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </RowDetailsTemplate>
  );
};

ScenarioRowDetails.propTypes = {
  scenario: scenarioDataShape.isRequired,
  deviceData: deviceDataShape,
  onDuplicate: PropTypes.func.isRequired,
  onTrigger: PropTypes.func.isRequired,
};

ScenarioRowDetails.defaultProps = {
  deviceData: null,
};

module.exports = compose(
  withDeviceData('scenario.device_id'),
  withEntitiesInformation,
)(ScenarioRowDetails);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/scenarios/ScenarioRowDetails.js